import { useGetAllMySuppliers } from '@/api/supplier.api';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import SaveIcon from '@/assets/icons/save-02.svg?react';
import CarRentalCategoriesList from '@/components/carRental/categories/CarRentalCategoriesList';
import StyledButton from '@/components/styled/StyledButton';
import StyledButtonLoader from '@/components/styled/StyledButtonLoader';
import StyledContainer from '@/components/styled/StyledContainer';
import StyledFormHeaderWithBreadcrumb from '@/components/styled/StyledFormHeaderWithBreadcrumb';
import StyledStepper from '@/components/styled/StyledStepper';
import HorizontalLine from '@/components/ui/HorizontalLine';
import CarRentalConfiguration from '@/components/carRental/CarRentalConfiguration';
import { PATHS } from '@/constants/Paths';
import { buildBenefitsListSearch } from '@/utils/searchParams/buildBenefitsListSearch';
import { getListSearchFromLocation } from '@/utils/searchParams/getListSearchFromLocation';
import { useCreateCarRental, useUpdateCarRental } from '@/hooks/useCarRental';
import { SupplierStatus } from '@/types/suppliers/SupplierStatus';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { ArrowRightIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import { WEEKDAYS } from '@/constants/weekdays';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { CAR_RENTAL_STEPS } from '@/constants/steppers/carRentalSteps';
import {
  CarRentalFormDataWithTranslations,
  CarRentalSchemaWithTranslations,
} from '@/i18n/translation-type-utils';
import { useResourceLanguage } from '@/hooks/useResourceLanguage';
import { useTranslateFields } from '@/hooks/useTranslate';
import LanguageSwitchWithTranslateBtn from '@/components/translation/LanguageSwitchWithTranslateBtn';
import CarRentalPricingSummary from '@/components/carRental/CarRentalPricingSummary';
import { MarginRateSchema } from '@/types/common/margin-rate.schema';

type SubmitFormType = (
  isDraft?: boolean,
) => SubmitHandler<CarRentalFormDataWithTranslations>;

const CreateCarRentalPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();

  const methods = useForm<CarRentalFormDataWithTranslations>({
    resolver: zodResolver(CarRentalSchemaWithTranslations),
    mode: 'onChange',
    defaultValues: {
      name: undefined,
      supplier: undefined,
      currency: undefined,
      country: undefined,
      marginRate: undefined,
      displayGlobalForm: 'false',
      shouldLimitInsurance: 'false',
      insurances: [
        {
          name: undefined,
          shortDescription: undefined,
          longDescription: undefined,
          amount: undefined,
          billingType: undefined,
        },
      ],
      taxes: [
        {
          name: undefined,
          required: false,
          amount: undefined,
          billingType: undefined,
        },
      ],
      options: [
        {
          name: undefined,
          description: undefined,
          amount: undefined,
          billingType: undefined,
        },
      ],
      oneWayFees: [
        {
          departureCity: undefined,
          arrivalCity: undefined,
          description: undefined,
          amount: undefined,
        },
      ],
      carCategories: [
        {
          displayCategoryOptions: 'false',
          images: [],
          tarifs: [
            {
              periods: [
                {
                  applicationDays: WEEKDAYS,
                },
              ],
              pricePartitions: [
                {
                  from: 1,
                  to: 1,
                },
              ],
            },
          ],
          insurances: [{}],
          options: [{}],
          taxes: [{}],
          oneWayFees: [{}],
        },
      ],
    },
  });

  const {
    control,
    getValues,
    formState: { isValid, errors },
    handleSubmit,
    setValue,
  } = methods;

  const currentName = useWatch({ control, name: 'name' });
  const selectedSupplier = useWatch({ control, name: 'supplier' });
  const selectedCountry = useWatch({ control, name: 'country' });

  const [benefitId, setBenefitId] = useState<string | null>(null);

  const { data: suppliers } = useGetAllMySuppliers();
  const { mutateAsync: createCarRental } = useCreateCarRental();
  const { mutateAsync: updateCarRental } = useUpdateCarRental();

  const [currStep, setCurrStep] = useState(0);

  const errorsLength = useMemo(() => Object.keys(errors).length, [errors]);

  const { language, setLanguage } = useResourceLanguage();
  const { translateFields, isTranslating } = useTranslateFields({
    targetLanguage: language,
    control,
    setValue,
    fields: ['benefit.name'] as const,
    nestedFields: {
      carCategories: [
        'category',
        'model',
        'description',
        'included',
        'notIncluded',
      ],
      insurances: ['name', 'shortDescription', 'longDescription'],
      options: ['name', 'description'],
      taxes: ['name'],
      oneWayFees: ['name', 'description'],
    },
  });

  const categoryErrorIndex = (() => {
    if (errors.carCategories) {
      return +Object.keys(errors.carCategories)[0];
    }
    return null;
  })();

  const { toastAsync } = useToast();

  const submitForm: SubmitFormType =
    (isDraft = true) =>
    async (data) => {
      const formData = new FormData();
      const addIf = (value: string | Blob | undefined, key: string) => {
        if (value !== undefined) formData.append(key, value);
      };
      if (!isDraft) formData.append('status', SupplierStatus.PUBLISHED);
      addIf(data.name, 'name');
      addIf(data.supplier?.value, 'supplierId');
      addIf(data.currency?.value, 'currency');
      addIf(data.country?.value, 'country');
      const parsedMarginRate = MarginRateSchema.safeParse(data.marginRate);
      if (parsedMarginRate.success && parsedMarginRate.data !== undefined)
        formData.append('marginRate', String(parsedMarginRate.data));
      addIf(data.displayGlobalForm, 'displayGlobalForm');
      addIf(data.shouldLimitInsurance, 'shouldLimitInsurance');
      if (data.insurances)
        formData.append('insurances', JSON.stringify(data.insurances));
      if (data.taxes) formData.append('taxes', JSON.stringify(data.taxes));
      if (data.options)
        formData.append('options', JSON.stringify(data.options));
      if (data.oneWayFees)
        formData.append('oneWayFees', JSON.stringify(data.oneWayFees));
      if (data.carCategories)
        formData.append(
          'carCategories',
          JSON.stringify(
            data.carCategories.map((carCategory) => ({
              ...carCategory,
              displayCategoryOptions:
                carCategory.displayCategoryOptions === 'true',
              pricings: carCategory.tarifs,
              tarifs: undefined,
              numberOfFiles: (carCategory.images ?? []).length,
            })),
          ),
        );
      data.carCategories
        .flatMap((category) => category.images)
        .forEach((image) => {
          if (image) formData.append('files', image);
        });
      formData.append('translations', JSON.stringify(data.translations));
      if (!benefitId) {
        const result = await toastAsync(createCarRental(formData), {
          success: t('carRental.create.success'),
        });
        setBenefitId(result.data.benefitId);
      } else {
        await toastAsync(updateCarRental({ benefitId, data: formData }), {
          success: t('carRental.update.success'),
        });
      }
      if (!isDraft)
        navigate(
          PATHS.home.benefits.list +
            getListSearchFromLocation(location, buildBenefitsListSearch),
        );
    };

  return (
    <FormProvider {...methods}>
      <StyledContainer>
        <StyledFormHeaderWithBreadcrumb
          title={currentName || t('carRental.create.title')}
          breadcrumb={
            <StyledButton
              variant="link"
              title={t('carRental.create.backToList')}
              className="text-sm text-[#3D3D3D] pl-0"
              onClick={() =>
                navigate(
                  PATHS.home.benefits.list +
                    getListSearchFromLocation(
                      location,
                      buildBenefitsListSearch,
                    ),
                )
              }
              icon={<ArrowLeftIcon />}
            />
          }
        >
          <StyledButton
            title={t('carRental.create.cancel')}
            onClick={() =>
              navigate(
                PATHS.home.benefits.list +
                  getListSearchFromLocation(location, buildBenefitsListSearch),
              )
            }
            variant="outlined"
            className="font-semibold gap-1 px-3 py-2"
          />
          <StyledButtonLoader
            title={t('common.saveAsDraft')}
            successTitle={t('common.draftSaved')}
            disabled={
              currentName == undefined ||
              selectedSupplier == undefined ||
              selectedCountry == undefined
            }
            onClick={() => {
              submitForm(true)(getValues());
            }}
            className="font-semibold gap-1 px-3 py-2"
            icon={<SaveIcon className="w-5 h-5" />}
          />
        </StyledFormHeaderWithBreadcrumb>
        <StyledStepper
          activeStep={currStep}
          steps={getTranslatedConst(CAR_RENTAL_STEPS)}
          setStep={(step: number) => setCurrStep(step)}
        />
        <HorizontalLine />
        <LanguageSwitchWithTranslateBtn
          translateFields={() =>
            translateFields({
              allNestedFields: true,
              translateToAllLocales: true,
            })
          }
          isTranslating={isTranslating}
          language={language}
          setLanguage={setLanguage}
        />
        {currStep === 0 ? (
          <>
            <CarRentalConfiguration
              control={control}
              suppliers={suppliers}
              isEditEnabled={true}
              language={language}
              translateFields={(fields) =>
                translateFields({ fieldsToTranslate: fields })
              }
              isTranslating={isTranslating}
              errorsLength={errorsLength}
            />
            <div className="flex flex-row-reverse p-6">
              <StyledButton
                title={t('carRental.create.addCategories')}
                onClick={() => {
                  setCurrStep(1);
                  window.scrollTo(0, 0);
                }}
                disabled={
                  currentName == undefined ||
                  selectedSupplier == undefined ||
                  selectedCountry == undefined
                }
                className="font-semibold gap-1 px-3 py-2"
                icon={<ArrowRightIcon className="w-5 h-5" />}
                iconPosition="right"
              />
            </div>
          </>
        ) : currStep === 1 ? (
          <>
            <CarRentalCategoriesList
              control={control}
              categoryErrorIndex={categoryErrorIndex}
              language={language}
            />
            <div className="flex flex-row-reverse p-6">
              <StyledButtonLoader
                title={t('common.saveAsDraft')}
                successTitle={t('common.draftSaved')}
                disabled={
                  currentName == undefined ||
                  selectedSupplier == undefined ||
                  selectedCountry == undefined
                }
                onClick={() => {
                  submitForm(true)(getValues());
                }}
                className="font-semibold gap-1 px-3 py-2"
                icon={<SaveIcon className="w-5 h-5" />}
              />
            </div>
            <div className="flex flex-row-reverse p-6">
              <StyledButtonLoader
                title={t('carRental.create.publish')}
                className="font-semibold gap-1 px-3 py-2"
                successTitle={t('carRental.create.publishedSuccess')}
                hasSucceeded={!errorsLength}
                errorTitle={t('carRental.create.requiredFieldsError')}
                onClick={handleSubmit(submitForm(false))}
                icon={
                  <PlusIcon
                    className={clsx('w-5 h-5', {
                      'fill-white': isValid,
                      'fill-gray-400': !isValid,
                    })}
                  />
                }
              />
            </div>
            <div className="flex flex-row-reverse px-6 pb-6 md:w-fit md:sticky md:left-full md:bottom-0">
              <StyledButton
                title={t('common.pricingSummary')}
                icon={<ArrowRightIcon className="w-5 h-5" />}
                iconPosition="right"
                className="font-semibold gap-1 px-3 py-2"
                onClick={() => {
                  setCurrStep(2);
                  window.scrollTo(0, 0);
                }}
              />
            </div>
          </>
        ) : (
          <>
            <CarRentalPricingSummary control={control} />
            <div className="flex flex-row-reverse px-6 pb-6">
              <StyledButtonLoader
                title={t('common.saveAsDraft')}
                successTitle={t('common.draftSaved')}
                disabled={
                  currentName == undefined ||
                  selectedSupplier == undefined ||
                  selectedCountry == undefined
                }
                onClick={() => {
                  submitForm(true)(getValues());
                }}
                className="font-semibold gap-1 px-3 py-2"
                icon={<SaveIcon className="w-5 h-5" />}
              />
            </div>
            <div className="flex flex-row-reverse p-6 md:sticky md:bottom-0 z-10 bg-white">
              <StyledButtonLoader
                title={t('carRental.create.publish')}
                className="font-semibold gap-1 px-3 py-2"
                successTitle={t('carRental.create.publishedSuccess')}
                hasSucceeded={!errorsLength}
                errorTitle={t('carRental.create.requiredFieldsError')}
                onClick={handleSubmit(submitForm(false))}
                icon={
                  <PlusIcon
                    className={clsx('w-5 h-5', {
                      'fill-white': isValid,
                      'fill-gray-400': !isValid,
                    })}
                  />
                }
                iconPosition="right"
              />
            </div>
          </>
        )}
      </StyledContainer>
    </FormProvider>
  );
};

export default CreateCarRentalPage;
